import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;
const DESKTOP_MEDIA = '(min-width: 769px)';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    // Close menu immediately when clicking on any link
    this.addEventListener('click', (event) => {
      if (event.target instanceof HTMLAnchorElement || (event.target instanceof Element && event.target.closest('a'))) {
        this.#deactivate(this.#state.activeItem);
      }
    }, {
      signal: this.#abortController.signal,
    });

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    this.#unlockBodyScroll();
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /** @type {{ locked: boolean; originalOverflow: string }} */
  #scrollLockState = {
    locked: false,
    originalOverflow: '',
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * @returns {boolean}
   */
  #isDesktop() {
    return typeof window !== 'undefined' && window.matchMedia && window.matchMedia(DESKTOP_MEDIA).matches;
  }

  /* Use overflow only so header stays visible (Safari: position:fixed + top:-scrollY hid the header). */
  #lockBodyScroll() {
    if (!this.#isDesktop() || this.#scrollLockState.locked) return;
    const body = document.body;
    this.#scrollLockState.originalOverflow = body.style.overflow || '';
    body.style.overflow = 'hidden';
    this.#scrollLockState.locked = true;
  }

  #unlockBodyScroll() {
    if (!this.#scrollLockState.locked) return;
    const body = document.body;
    body.style.overflow = this.#scrollLockState.originalOverflow;
    this.#scrollLockState.locked = false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    // Check if this is the Shop link - if so, don't show submenu
    const linkTitle = item.querySelector('.mega-menu__link-title');
    const linkText = linkTitle ? linkTitle.textContent.trim() : '';
    
    // Check if this is the main Shop link by checking if it's the first item in the parent list
    if (linkText === 'Shop' || linkText === 'shop') {
      const parentList = item.closest('.mega-menu__list_parent');
      if (parentList) {
        const firstItem = parentList.querySelector('.shop-link-name:first-child');
        const isFirstShopLink = firstItem === item;
        
        if (isFirstShopLink) {
          // Check if Shop link has children with actual content
          const shopChildren = document.querySelectorAll('.shop-link-children');
          let hasAnyContent = false;
          
          shopChildren.forEach(child => {
            if (child instanceof HTMLElement) {
              const childDiv = child.querySelector('div');
              const hasContent = childDiv && (
                childDiv.querySelector('ul') || 
                childDiv.querySelector('a') ||
                childDiv.textContent.trim().length > 0
              );
              
              if (hasContent) {
                hasAnyContent = true;
              }
            }
          });
          
          // Only prevent Shop menu activation if there's no content
          if (!hasAnyContent) {
            this.removeAttribute('data-animating');
            this.dataset.overflowExpanded = 'false';
            this.style.setProperty('--submenu-height', '0px');
            this.style.setProperty('--submenu-opacity', '0');
            
            // Hide all children menus for Shop (only if empty)
            shopChildren.forEach(child => {
              if (child instanceof HTMLElement) {
                child.style.display = 'none';
              }
            });
            
            // Ensure no aria-expanded is set
            if (item instanceof HTMLElement) {
              item.ariaExpanded = 'false';
            }
            
            return; // Don't proceed with normal activation
          }
          // If Shop has content, continue with normal activation flow
        }
      }
    }

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    item.ariaExpanded = 'true';
    this.ariaExpanded = 'true';
    this.#lockBodyScroll();

    let submenu = findSubmenu(item);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
    
    const columns = document.querySelectorAll('.mega-menu__column');

    columns.forEach(column => {
        const divElement = column.querySelectorAll('div');
        if (divElement) {
            divElement.forEach(divElements => {
                const spanElement = divElements.querySelector('.mega-menu__link .mega-menu__link-title');
            
                if (spanElement && spanElement instanceof HTMLElement) {
                    spanElement.style.fontWeight = 'bold';
                }
            });
        } 
        
    });
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Close the menu (e.g. when backdrop is clicked). Public API for external scripts.
   */
  close() {
    this.#debouncedDeactivate.cancel();
    this.#deactivate(this.#state.activeItem);
  }

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    // On desktop Safari, ignore blur/focusout so overlay or focus shift doesn't close the menu
    // and cause open/close glitch or leave scroll stuck. Close only on pointerleave.
    if (event && event instanceof FocusEvent && this.#isDesktop()) return;

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.#unlockBodyScroll();
    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  // Support both: event on list item (li) or on link/span inside (closest finds the link)
  return element.closest('[ref="menuitem"]') || element.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
